import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authApi, saveTokens, clearTokens, getRefresh, api } from '@/lib/api';

const AuthCtx = createContext(null);

export function roleHomePath(role) {
  if (role === 'platform_owner' || role === 'admin') return '/admin';
  if (role === 'agent') return '/agent';
  if (role === 'bailleur') return '/bailleur';
  if (role === 'locataire') return '/locataire';
  return '/';
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('lb_user') || 'null'); } catch { return null; }
  });
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const access = localStorage.getItem('lb_token');
    const rt = getRefresh();

    // Aucun token → utilisateur non connecté, session propre.
    if (!access && !rt) {
      setUser(null);
      setLoading(false);
      return;
    }

    // 1) Tentative directe /auth/me (l'intercepteur axios rafraîchira automatiquement
    //    l'access token si nécessaire, car /auth/me n'est plus exclu du refresh).
    try {
      const { data } = await authApi.me();
      setUser(data);
      localStorage.setItem('lb_user', JSON.stringify(data));
      setLoading(false);
      return;
    } catch (e) {
      // 401 déjà géré par l'intercepteur : si on arrive ici, c'est que le refresh a
      // aussi échoué OU que c'est une autre erreur. On tente un dernier refresh explicite.
      console.debug('[Auth] /auth/me failed at boot, falling back to explicit refresh:', e?.response?.status);
    }

    // 2) Fallback : refresh explicite puis retry /auth/me. Ne nettoie le storage que
    //    si le refresh token est réellement invalide/expiré.
    if (rt) {
      try {
        const { data } = await api.post('/auth/refresh', { refresh_token: rt });
        saveTokens(data.access_token, data.refresh_token);
        const meRes = await authApi.me();
        setUser(meRes.data);
        localStorage.setItem('lb_user', JSON.stringify(meRes.data));
        setLoading(false);
        return;
      } catch (e2) {
        // Refresh token réellement invalide → session terminée
        console.debug('[Auth] explicit refresh failed, ending session:', e2?.response?.status);
        clearTokens();
        setUser(null);
        setLoading(false);
        return;
      }
    }

    // 3) Pas de refresh token, /me a échoué → session terminée
    clearTokens();
    setUser(null);
    setLoading(false);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (identifier, password, expected_role) => {
    const { data } = await authApi.login(identifier, password, expected_role);
    if (data.mfa_required) {
      return { mfa_required: true, challenge_token: data.challenge_token };
    }
    saveTokens(data.access_token, data.refresh_token);
    localStorage.setItem('lb_user', JSON.stringify(data.user));
    setUser(data.user);
    return { user: data.user };
  };

  const completeMfa = async (challenge_token, code) => {
    const { data } = await authApi.mfaVerify(challenge_token, code);
    saveTokens(data.access_token, data.refresh_token);
    localStorage.setItem('lb_user', JSON.stringify(data.user));
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    const rt = getRefresh();
    try {
      await authApi.logoutServer(rt);
    } catch (e) {
      console.debug('[Auth] logout best-effort failed:', e?.response?.status);
    }
    clearTokens();
    setUser(null);
    window.location.href = '/';
  };

  const setUserData = (u) => {
    setUser(u);
    localStorage.setItem('lb_user', JSON.stringify(u));
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, completeMfa, logout, refresh, setUserData }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
